'use client';

import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';

import type { ChargebeeEvent } from '@/types/events';
import { explorerAnalytics } from '@/lib/analytics';
import { useEventExplorerStore } from '@/stores/useEventExplorerStore';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Button } from '@components/components/ui/button';
import { EventEmptyState } from './EventEmptyState';
import { EventFilterBar } from './EventFilterBar';
import { EventListItem } from './EventListItem';
import { EventLiveTailIndicator } from './EventLiveTailIndicator';

interface EventListPanelProps {
  events: ChargebeeEvent[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  selectedEventId: string | null;
  onSelectEvent: (id: string | null) => void;
  onLoadMore: () => void;
  onRefresh: () => void;
  onPollRefresh: () => Promise<void>;
}

export const EventListPanel: React.FC<EventListPanelProps> = ({
  events,
  isLoading,
  isLoadingMore,
  error,
  hasMore,
  selectedEventId,
  onSelectEvent,
  onLoadMore,
  onRefresh,
  onPollRefresh,
}) => {
  const { liveBuffer, mergeLiveBuffer } = useEventExplorerStore();
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: hasMore ? events.length + 1 : events.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 80,
    overscan: 10,
  });

  // Infinite scroll trigger
  const virtualItems = rowVirtualizer.getVirtualItems();
  useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1];
    if (!lastItem) return;

    if (
      lastItem.index >= events.length - 5 &&
      hasMore &&
      !isLoadingMore &&
      !isLoading
    ) {
      onLoadMore();
    }
  }, [
    virtualItems,
    hasMore,
    isLoadingMore,
    isLoading,
    events.length,
    onLoadMore,
  ]);

  // Auto-merge live buffer if scrolled to top
  useEffect(() => {
    if (liveBuffer.length > 0 && parentRef.current) {
      const scrollTop = parentRef.current.scrollTop;
      if (scrollTop < 10) {
        explorerAnalytics.eventLiveBufferMerged({
          eventCount: liveBuffer.length,
        });
        mergeLiveBuffer();
      }
    }
  }, [liveBuffer.length, mergeLiveBuffer]);

  // Keyboard navigation: Arrow Up/Down to move between events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (events.length === 0) return;

      e.preventDefault();
      const currentIndex = selectedEventId
        ? events.findIndex((ev) => ev.id === selectedEventId)
        : -1;

      let nextIndex: number;
      if (e.key === 'ArrowDown') {
        nextIndex = currentIndex < events.length - 1 ? currentIndex + 1 : 0;
      } else {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : events.length - 1;
      }

      onSelectEvent(events[nextIndex].id);
      rowVirtualizer.scrollToIndex(nextIndex, { align: 'auto' });
    },
    [events, selectedEventId, onSelectEvent, rowVirtualizer]
  );

  return (
    <div className="flex h-full flex-col">
      <EventFilterBar
        eventCount={events.length}
        onRefresh={onRefresh}
        onPollRefresh={onPollRefresh}
      />

      {liveBuffer.length > 0 && (
        <EventLiveTailIndicator
          count={liveBuffer.length}
          onMerge={mergeLiveBuffer}
        />
      )}

      {isLoading ? (
        <EventEmptyState type="loading" />
      ) : error ? (
        <EventEmptyState type="error" message={error} onRetry={onRefresh} />
      ) : events.length === 0 ? (
        <EventEmptyState type="empty" />
      ) : (
        <div
          ref={parentRef}
          role="listbox"
          aria-label="Event list"
          className="flex-1 overflow-auto bg-white outline-none"
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualItems.map((virtualRow) => {
              const isLoaderRow = virtualRow.index >= events.length;

              if (isLoaderRow) {
                return (
                  <div
                    key="loader"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                    className="flex items-center justify-center"
                  >
                    {isLoadingMore ? (
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-shade-teal-500" />
                        Loading more...
                      </div>
                    ) : (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={onLoadMore}
                        className="text-shade-teal-600 hover:text-shade-teal-700"
                      >
                        Load more
                      </Button>
                    )}
                  </div>
                );
              }

              const event = events[virtualRow.index];
              return (
                <div
                  key={event.id}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <EventListItem
                    event={event}
                    isSelected={event.id === selectedEventId}
                    onClick={() => {
                      explorerAnalytics.eventSelected({
                        eventType: event.event_type,
                      });
                      onSelectEvent(event.id);
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
